import { CheckCircle, Close, RadioButtonUnchecked, RocketLaunch } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { OnboardingChecklist } from '@fsp/types';
import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ONBOARDING_TASKS, OnboardingTask } from '../config/onboardingTasks';
import { usePlan } from '../contexts/PlanContext';
import { OnboardingSpotlight } from './OnboardingSpotlight';

export const OnboardingSidebar: React.FC = () => {
  const { currentPlan, chapters } = usePlan();
  const location = useLocation();
  const [activeTask, setActiveTask] = useState<OnboardingTask | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [dismissedForPath, setDismissedForPath] = useState<string | null>(null);

  const checklist: OnboardingChecklist | null = currentPlan?.onboardingChecklist
    ? JSON.parse(currentPlan.onboardingChecklist)
    : null;

  /**
   * Determines if user is already on the task's target page
   */
  const isOnTaskPage = React.useCallback(
    (task: OnboardingTask): boolean => {
      // Chapter-based detection
      if (task.chapterType) {
        // Extract chapterId from URL path like /plans/:planId/new/chapters/:chapterId
        const chapterIdMatch = location.pathname.match(/\/chapters\/([^/]+)/);
        if (chapterIdMatch) {
          const currentChapterId = chapterIdMatch[1];
          const currentChapter = chapters.find((ch) => ch.id === currentChapterId);
          return currentChapter?.type === task.chapterType;
        }
        return false;
      }

      // Route-based detection
      if (task.routePattern) {
        return location.pathname.includes(task.routePattern);
      }

      return false;
    },
    [location.pathname, chapters]
  );

  // Auto-open secondary task when user navigates to the task page
  useEffect(() => {
    if (!checklist) return;

    // Reset dismissed state when path changes
    if (dismissedForPath && dismissedForPath !== location.pathname) {
      setDismissedForPath(null);
    }

    // Only auto-open if not dismissed for this path
    if (dismissedForPath === location.pathname) return;

    // Find the first incomplete task that the user is currently on
    const currentTask = ONBOARDING_TASKS.find((task) => !checklist[task.id] && isOnTaskPage(task));

    if (currentTask) {
      setActiveTask(currentTask);
    }
  }, [location.pathname, chapters, checklist, isOnTaskPage, dismissedForPath]);

  if (!checklist) return null;

  const completedTasks = Object.values(checklist).filter((v) => v).length;
  const totalTasks = Object.keys(checklist).length;
  const progressPercentage = (completedTasks / totalTasks) * 100;
  const allTasksCompleted = completedTasks === totalTasks;

  const handleTaskClick = (task: OnboardingTask) => {
    setActiveTask(task);
  };

  const handleCloseSpotlight = () => {
    setActiveTask(null);
    setDismissedForPath(location.pathname);
  };

  const handleClose = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('onboarding');
    setSearchParams(newParams);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 3, pt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RocketLaunch sx={{ color: 'primary.main' }} />
          <Typography variant="h6">New Plan Checklist</Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
            },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Complete these steps to setup your new plan
          </Typography>
          <Typography variant="body2" fontWeight="600">
            {completedTasks}/{totalTasks}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          sx={{
            height: 8,
            borderRadius: 1,
            backgroundColor: 'action.hover',
            '& .MuiLinearProgress-bar': {
              borderRadius: 1,
            },
          }}
        />
      </Box>

      {allTasksCompleted && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: 'success.light',
            border: 1,
            borderColor: 'success.main',
            textAlign: 'center',
          }}
        >
          <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'success.dark' }}>
            Congratulations!
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            You have successfully completed your plan checklist tasks. Continue to use the plan
            editor to further develop and refine your food safety plan.
          </Typography>
          <Button
            variant="contained"
            color="success"
            onClick={handleClose}
            startIcon={<Close />}
            fullWidth
          >
            Close Onboarding
          </Button>
        </Box>
      )}

      <List sx={{ pt: 0 }} dense>
        {ONBOARDING_TASKS.map((task) => {
          const isCompleted = checklist[task.id];

          return (
            <ListItem
              key={task.id}
              onClick={() => !isCompleted && handleTaskClick(task)}
              sx={{
                borderRadius: 1,
                mb: 1,
                opacity: isCompleted ? 0.6 : 1,
                bgcolor: isCompleted ? 'action.hover' : 'transparent',
                cursor: isCompleted ? 'default' : 'pointer',
                '&:hover': isCompleted
                  ? {}
                  : {
                      bgcolor: 'action.hover',
                    },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {isCompleted ? (
                  <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
                ) : (
                  <RadioButtonUnchecked sx={{ fontSize: 20, color: 'action.active' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={task.title}
                secondary={task.description}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: {
                    fontWeight: isCompleted ? 400 : 600,
                    textDecoration: isCompleted ? 'line-through' : 'none',
                  },
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                }}
              />
            </ListItem>
          );
        })}
      </List>

      <OnboardingSpotlight
        open={activeTask !== null}
        onClose={handleCloseSpotlight}
        targetSelector={
          activeTask
            ? isOnTaskPage(activeTask)
              ? activeTask.secondaryTargetSelector
              : activeTask.primaryTargetSelector
            : undefined
        }
        helperText={
          activeTask
            ? isOnTaskPage(activeTask)
              ? activeTask.secondaryHelperText
              : activeTask.primaryHelperText
            : ''
        }
        title={activeTask?.title || ''}
      />
    </Box>
  );
};

export default OnboardingSidebar;
